import React from "react";
import { LegalLayout, LegalSection } from "../components/LegalLayout";
import { COMPANY } from "../lib/constants";

const SECTIONS = [
  { id: "who", label: "1. Who can use the Services" },
  { id: "accounts", label: "2. Accounts" },
  { id: "fees", label: "3. Plans, fees and taxes" },
  { id: "credits", label: "4. Credits" },
  { id: "payments", label: "5. Payments" },
  { id: "non-payment", label: "6. Non-payment" },
  { id: "your-data", label: "7. Your data" },
  { id: "acceptable-use", label: "8. Acceptable use" },
  { id: "clinical", label: "9. Clinical responsibility" },
  { id: "cmh", label: "10. Atikxzio CMH" },
  { id: "availability", label: "11. Availability and support" },
  { id: "ip", label: "12. Intellectual property" },
  { id: "liability", label: "13. Limitation of liability" },
  { id: "termination", label: "14. Termination" },
  { id: "law", label: "15. Governing law" },
  { id: "contact", label: "16. Contact and grievances" },
];

export default function Terms() {
  return (
    <LegalLayout
      title="Terms &amp; Conditions"
      sections={SECTIONS}
      seo={{
        title: "Terms & Conditions | Priyajit Systems Private Limited",
        description: "Terms and conditions governing the use of priyajitsystems.in, atikxzio.com and the Atikxzio HMS and Atikxzio CMH services provided by Priyajit Systems Private Limited.",
      }}
    >
      <p className="text-[17px] leading-[1.75] text-slate-700">
        These Terms govern your use of www.priyajitsystems.in, atikxzio.com and the products Atikxzio HMS and Atikxzio CMH
        (together, the "Services"), provided by <strong>Priyajit Systems Private Limited</strong> (CIN {COMPANY.cin},
        GSTIN {COMPANY.gstin}), registered office A-13, Upper Ground Floor, Graphix Tower 2, Sector 62, Noida,
        Gautam Buddha Nagar, Uttar Pradesh 201301, India ("we", "us"). By using the Services or making a payment, you agree
        to these Terms.
      </p>

      <LegalSection id="who" heading="1. Who can use the Services.">
        <p>Atikxzio HMS is for registered hospitals, nursing homes, clinics and diagnostic centres in India and their authorised staff. You must be 18 or older and able to sign a binding contract on behalf of your organisation.</p>
      </LegalSection>

      <LegalSection id="accounts" heading="2. Accounts.">
        <p>You are responsible for activity under your organisation's accounts. Keep passwords private; we never ask for your password. Tell us at once at {COMPANY.email} if you suspect misuse.</p>
      </LegalSection>

      <LegalSection id="fees" heading="3. Plans, fees and taxes.">
        <p>Plans, usage charges, credits and add-ons are listed on our Pricing page (/pricing) and in your order form. All prices are in Indian Rupees and exclude GST, which is charged extra. Plans are billed in advance, monthly or annually. Hospital plans include a usage charge per IPD patient-day and per Day Care session, counted at the midnight census (IST). A one-time setup fee applies to monthly plans and is waived on annual payment. We may change prices with 30 days' written notice; changes apply from your next billing period.</p>
      </LegalSection>

      <LegalSection id="credits" heading="4. Credits.">
        <p>Credits are prepaid units (1 credit = ₹1) used for messages and extra AI reads. Plan credits are added each month; purchased credits are added on payment with a GST invoice. Unused purchased credits are refundable on closing your account, minus GST.</p>
      </LegalSection>

      <LegalSection id="payments" heading="5. Payments.">
        <p>Payments are processed by RBI-authorised payment aggregators such as Cashfree Payments. We do not store card, UPI PIN or net-banking details. A GST invoice is issued for every payment.</p>
      </LegalSection>

      <LegalSection id="non-payment" heading="6. Non-payment.">
        <p>If a payment fails or your credit balance runs out, we will remind you. After 7 days of negative balance, non-clinical features such as messaging, AI reading and reports may pause. Clinical work, existing patient records and your data export never stop.</p>
      </LegalSection>

      <LegalSection id="your-data" heading="7. Your data.">
        <p>You own all data you enter, including patient records. For patient data we act as a data processor on your instructions, as described in our Privacy Policy. You can export your data at any time.</p>
      </LegalSection>

      <LegalSection id="acceptable-use" heading="8. Acceptable use.">
        <p>Do not misuse the Services: no unlawful content, no attempts to break security, no reselling without a written agreement, and no storing Aadhaar numbers or card numbers in free-text fields.</p>
      </LegalSection>

      <LegalSection id="clinical" heading="9. Clinical responsibility.">
        <p>Atikxzio HMS is a record-keeping and workflow tool. All diagnoses, prescriptions and treatment decisions remain the responsibility of the treating doctor and the hospital. AI-assisted features always require human review before anything is saved.</p>
      </LegalSection>

      <LegalSection id="cmh" heading="10. Atikxzio CMH.">
        <p>Career guidance and wellbeing content is not medical or psychiatric treatment. In a mental-health emergency, call Tele-MANAS on 14416 or go to the nearest hospital.</p>
      </LegalSection>

      <LegalSection id="availability" heading="11. Availability and support.">
        <p>We aim for high availability but do not guarantee uninterrupted service. Planned maintenance is announced in advance. Support is available Monday to Saturday, 9:30 am to 6:30 pm IST, in English and Hindi.</p>
      </LegalSection>

      <LegalSection id="ip" heading="12. Intellectual property.">
        <p>The software, designs and the brands Atikxzio and Priyajit Systems belong to us. You receive a limited, non-transferable right to use the Services during your subscription.</p>
      </LegalSection>

      <LegalSection id="liability" heading="13. Limitation of liability.">
        <p>To the extent the law allows, our total liability for any claim is limited to the fees you paid us in the 12 months before the claim. We are not liable for indirect or consequential losses.</p>
      </LegalSection>

      <LegalSection id="termination" heading="14. Termination.">
        <p>You may cancel at any time as set out in our Refund &amp; Cancellation Policy. We may suspend accounts that breach these Terms after notice. On termination we keep your data available for export for 90 days, then delete it unless the law requires us to keep it.</p>
      </LegalSection>

      <LegalSection id="law" heading="15. Governing law.">
        <p>These Terms are governed by the laws of India. Courts at Gautam Buddha Nagar, Uttar Pradesh have exclusive jurisdiction.</p>
      </LegalSection>

      <LegalSection id="contact" heading="16. Contact and grievances.">
        <p>Questions: {COMPANY.email}, {COMPANY.phone}. Grievance Officer: {COMPANY.grievanceName}, {COMPANY.grievanceEmail}.</p>
      </LegalSection>
    </LegalLayout>
  );
}
